import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { FolderPlus, Plus, Check, Bookmark } from 'lucide-react';

const SaveToFolderButton = ({ resourceId, size = 'sm' }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [newName, setNewName] = useState('');
  const [saved, setSaved] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/resource-folders`); setFolders(res.data.folders || []); }
    catch (e) { /* ignore */ }
  }, []);

  const handleOpen = () => {
    if (!isAuthenticated) {
      toast.info('Sign in to save resources into referral folders.');
      navigate('/login');
      return;
    }
    load();
    setOpen(true);
  };

  const addTo = async (folder) => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/resource-folders/${folder.folder_id}/add`, { resource_id: resourceId });
      setSaved((s) => new Set(s).add(folder.folder_id));
      toast.success(`Saved to "${folder.name}".`);
    } catch (e) { toast.error('Could not save.'); } finally { setBusy(false); }
  };

  const createAndAdd = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/resource-folders`, { name: newName.trim() });
      const folder = res.data.folder;
      await axios.post(`${API_URL}/resource-folders/${folder.folder_id}/add`, { resource_id: resourceId });
      toast.success(`Saved to "${folder.name}".`);
      setNewName('');
      await load();
      setSaved((s) => new Set(s).add(folder.folder_id));
    } catch (e) { toast.error('Could not create folder.'); } finally { setBusy(false); }
  };

  return (
    <>
      <Button type="button" size={size} variant="outline" className="border-secondary text-secondary hover:bg-secondary hover:text-white" onClick={handleOpen} data-testid={`save-resource-${resourceId}`}>
        <Bookmark className="w-4 h-4 mr-1" /> Save
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Save to a Referral Folder</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {folders.length === 0 && <p className="text-sm text-gray-500">No folders yet — create one below.</p>}
            {folders.map((f) => (
              <button key={f.folder_id} onClick={() => addTo(f)} disabled={busy} className="w-full flex items-center justify-between px-4 py-3 rounded-lg border hover:border-primary text-left" data-testid={`folder-option-${f.folder_id}`}>
                <span className="text-gray-800">{f.name} <span className="text-xs text-gray-400">({f.item_count})</span></span>
                {saved.has(f.folder_id) ? <Check className="w-4 h-4 text-green-600" /> : <Plus className="w-4 h-4 text-gray-400" />}
              </button>
            ))}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Input placeholder="New folder name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-folder-name" />
            <Button className="bg-primary text-white" onClick={createAndAdd} disabled={busy || !newName.trim()} data-testid="create-folder-save"><FolderPlus className="w-4 h-4 mr-1" /> Create &amp; Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SaveToFolderButton;
